/**
 * The per-customer-site rows under an expanded Supplier Performance row —
 * Customer review 11 Sep (item 3/4, the old portal's m04b model).
 */
import { useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { SupplierProfile, SupplierRatingThreshold, OTIFGradeThreshold } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PerformanceTableRow, RowCallbacks } from './PerformanceTableRow';
import { useSiteBreakdown } from './useSiteBreakdown';
import { CodeBreakdownRows } from './CodeBreakdownRows';
import { BreakdownCode } from './useCodeBreakdown';
import { siteSplitsByCode } from './utils';

interface SiteBreakdownRowsProps extends RowCallbacks {
  supplier: SupplierProfile;
  thresholds: SupplierRatingThreshold[];
  /** CR-6269: OTIF % colour bands, threaded to every row. */
  otifBands?: OTIFGradeThreshold[];
  currency: string;
  columnCount: number;
  buildSiteRowParams: (siteId: number, supplierId: number) => Record<string, string | number | boolean>;
  /** CR-7288: params for one code under one site — the third tier. */
  buildCodeRowParams: (siteId: number, supplierId: number, code: BreakdownCode) => Record<string, string | number | boolean>;
}

export function SiteBreakdownRows({
  supplier,
  thresholds,
  otifBands = [],
  currency,
  columnCount,
  buildSiteRowParams,
  buildCodeRowParams,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: SiteBreakdownRowsProps) {
  const sites = supplier.customer_sites ?? [];
  const [openSites, setOpenSites] = useState<Set<number>>(new Set());
  const toggleSite = (siteId: number) => setOpenSites((prev) => {
    const next = new Set(prev);
    if (next.has(siteId)) next.delete(siteId); else next.add(siteId);
    return next;
  });
  const { data, loading, error, refetch } = useSiteBreakdown(
    supplier.company, sites, buildSiteRowParams, true,
  );

  const message = (content: ReactNode, key: string) => (
    <tr key={key} className="border-b bg-slate-50 dark:bg-slate-900">
      <td colSpan={columnCount} className="p-3 pl-12 text-xs text-muted-foreground">
        <div className="sticky left-12 inline-flex items-center gap-2">{content}</div>
      </td>
    </tr>
  );

  if (loading) {
    return message(
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading {supplier.company_name} at each of {sites.length} customer sites…
      </>,
      'loading',
    );
  }
  if (error) {
    return message(
      <>
        <span className="text-red-600">Could not load the per-site figures: {error}</span>
        <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => refetch()}>
          Retry
        </Button>
      </>,
      'error',
    );
  }

  return (
    <>
      {(data ?? []).flatMap(({ site, profile }): ReactNode[] => {
        if (!profile) {
          return [message(`${site.name}: no figures for ${supplier.company_name} in this period`, `empty-${site.id}`)];
        }
        // CR-7288: a site splits when there is MORE THAN ONE ROW beneath it.
        // 16 of the 20 relationships in the data hold one code and no uncoded
        // work, and expanding those to reveal a single identical row is noise
        // — the site row already names that code. Round 2: the test used to be
        // `supplier_codes.length > 1`, which excludes blanks by design and so
        // hid the `(no code)` row on a one-code site that had one.
        const splittable = siteSplitsByCode(site);
        const open = openSites.has(site.id);
        const rows: ReactNode[] = [
          <PerformanceTableRow
            key={`site-${supplier.company}-${site.id}`}
            supplier={profile}
            thresholds={thresholds}
            otifBands={otifBands}
            currency={currency}
            viewerIsSupplier={false}
            showSupplierSite={false}
            variant="site"
            site={site}
            expandable={splittable}
            expanded={open}
            onToggleExpand={splittable ? () => toggleSite(site.id) : undefined}
            onOpenGRNModal={onOpenGRNModal}
            onOpenNCRModal={onOpenNCRModal}
            onOpenPOLineModal={onOpenPOLineModal}
          />,
        ];
        if (splittable && open) {
          rows.push(
            <CodeBreakdownRows
              key={`codes-${supplier.company}-${site.id}`}
              supplierId={supplier.company}
              site={site}
              thresholds={thresholds}
              otifBands={otifBands}
              currency={currency}
              columnCount={columnCount}
              buildCodeRowParams={buildCodeRowParams}
              onOpenGRNModal={onOpenGRNModal}
              onOpenNCRModal={onOpenNCRModal}
              onOpenPOLineModal={onOpenPOLineModal}
            />,
          );
        }
        return rows;
      })}
    </>
  );
}
